/* ========================================================
   CART SERVICE
   Incapsula la logica di interazione con il carrello Shopify.
   ======================================================== */
class CartService {
    // Ottiene lo stato del carrello con /cart.js
    static async fetchCart() {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        return response.json();
    }

    // Aggiunge un prodotto al carrello con /cart/add.js
    static async addItem(formData) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        return response.json();
    }

    // Rimuove un item specifico (lineIndex) settando quantity = 0 con /cart/change.js
    static async removeLineItem(lineIndex) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.method = 'POST';
        config.body = JSON.stringify({
            line: lineIndex + 1,
            quantity: 0
        });

        const response = await fetch(`${routes.cart_change_url}`, config);
        return response.json();
    }

    // Cerca un item con una property dinamica e lo rimuove
    static async removeItemByProperty(propertyKey, propertyValue) {
        const cartData = await this.fetchCart();
        const lineIndex = cartData.items.findIndex(item =>
            item.properties && item.properties[propertyKey] === propertyValue
        );
        if (lineIndex !== -1) {
            await this.removeLineItem(lineIndex);
        }
    }
}

/* ========================================================
   PRODUCT FORM
   Custom element che gestisce l’interazione con la pagina.
   Usa CartService per rimuovere e aggiungere item
   e CartPropertiesStore per salvare le property in hidden input.
   ======================================================== */
class ProductForm extends HTMLElement {
    constructor() {
        super();

        this.body = document.querySelector('body');
        this.form = this.querySelector('.form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonSpinner = this.submitButton?.querySelector('.loading__spinner');

        this.itemId = this.dataset.lineitem;
        this.participant = this.dataset.participant;
        this.guest = this.dataset.guest;
        this.productHandle = this.querySelector('[name="handle"]')?.value || null;

        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
    }

    /* ===========================================
       Click sul pulsante "submit"
    ============================================ */
    async onSubmitHandler(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.loadingSubmitButton();

        try {
            // 1) Raccolgo i dati dal form
            const formData = new FormData();
            this.form.querySelectorAll('input').forEach(input => {
                if (input.name && !input.disabled) {
                    formData.append(input.name, input.value);
                }
            });

            // 2) Creazione della chiave dinamica per la property
            const propertyKey = `_program_property_name_${this.productHandle}`;
            const propertyValue = formData.get(`properties[${propertyKey}]`);

            // 3) Verifica se il prodotto è già nel carrello e rimuovilo
            if (propertyValue) {
                console.log(`Verifico se esiste un prodotto con property ${propertyKey}: ${propertyValue}`);
                await CartService.removeItemByProperty(propertyKey, propertyValue);
            }

            // 4) Ora aggiungo il nuovo prodotto
            console.log("Aggiunta del nuovo prodotto al carrello...");
            const responseData = await CartService.addItem(formData);
            if (responseData.status) {
                console.error('Errore durante l\'aggiunta al carrello:', responseData.description);
            } else {
                // 5) Aggiorna la UI dopo la modifica
                await this.updatePageTotals();

                const section = this.closest('.field-group--companion');
                const selectedCard = this.form.closest('.product-card-wrapper');
                if (section && selectedCard) {
                    this.updateProductCardSelection(section, selectedCard);
                }

                // 6) Salva la nuova property nel CartPropertiesStore
                const productId = formData.get('id');
                if (this.itemId && productId) {
                    const newPropertyName = `_participant_${this.participant}_guest_${this.guest}_program_${this.productHandle}`;
                    CartPropertiesStore.setProperty(this.itemId, newPropertyName, productId);
                }
            }
        } catch (error) {
            console.error('Errore in onSubmitHandler:', error);
        } finally {
            this.resetSubmitButton();
        }
    }

    /* ===========================================
       Rimuove l'articolo "guest" da una sezione
    ============================================ */
    async removeProductInSection(section) {
        const selectedProduct = section.querySelector('.product-card-wrapper--selected');
        if (!selectedProduct) {
            console.log('Nessun prodotto selezionato da rimuovere in questa sezione.');
            return;
        }

        const selectedFormProduct = selectedProduct.querySelector('product-form');
        if (!selectedFormProduct) {
            console.log('Nessun <product-form> trovato.');
            return;
        }

        const propertyValue = `_participant_${selectedFormProduct.dataset.participant}_guest_${selectedFormProduct.dataset.guest}_program_${this.productHandle}`;
        await this.removeProductFromCart(section, propertyValue);
    }

    /* ===========================================
       Rimuove un prodotto dal carrello
    ============================================ */
    async removeProductFromCart(section, propertyValue) {
        try {
            const propertyKey = `_program_property_name_${this.productHandle}`;
            await CartService.removeItemByProperty(propertyKey, propertyValue);

            this.updateProductCardSelection(section, null);
            CartPropertiesStore.removeProperties(this.itemId, [propertyValue]);
            await this.updatePageTotals();
        } catch (error) {
            console.error('Errore durante removeProductFromCart:', error);
        }
    }

    /* ===========================================
       Aggiorna i totali del carrello
    ============================================ */
    async updatePageTotals() {
        try {
            const response = await fetch('/cart');
            const html = await response.text();
            const newDocument = new DOMParser().parseFromString(html, 'text/html');

            const cartTotalElements = document.querySelectorAll('.totals');
            const newTotals = newDocument.querySelector('.totals');
            if (cartTotalElements.length > 0 && newTotals) {
                cartTotalElements.forEach(item => {
                    item.innerHTML = newTotals.innerHTML;
                });
            }

            const cartCountBubble = document.querySelector('.cart-count-bubble');
            const newCartCountBubble = newDocument.querySelector('.cart-count-bubble');
            if (cartCountBubble && newCartCountBubble) {
                cartCountBubble.innerHTML = newCartCountBubble.innerHTML;
            }
        } catch (error) {
            console.error('Errore in updatePageTotals:', error);
        }
    }

    /* ===========================================
       Selezione della card prodotto
    ============================================ */
    updateProductCardSelection(section, selectedCard) {
        const activeClass = 'product-card-wrapper--selected';
        const oldCard = section.querySelector(`.${activeClass}`);
        const oldInput = oldCard?.querySelector('.field input')
        const selectedInput = selectedCard?.querySelector('.field input')
        oldCard?.classList.remove(activeClass);
        oldInput?.setAttribute('checked', false)
        selectedCard?.classList.add(activeClass);
        selectedInput?.setAttribute('checked', true)
    }

    /* ===========================================
       Gestione del pulsante di submit
    ============================================ */
    loadingSubmitButton() {
        this.body.classList.add('loading');
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.buttonSpinner?.classList.remove('hidden');
    }

    resetSubmitButton() {
        this.body.classList.remove('loading');
        this.submitButton.classList.remove('loading');
        this.buttonSpinner?.classList.add('hidden');
        this.submitButton.removeAttribute('aria-disabled');
    }
}

/* ========================================================
   Registrazione del custom element <product-form>
   ======================================================== */
customElements.define('product-form', ProductForm);
